import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { FileText, Users, Sun, Moon, ArrowLeft, Calculator, LogOut, MessageSquare, KanbanSquare } from 'lucide-react';
import BlogAdmin from './BlogAdmin';
import LeadBoard from './LeadBoard';
import PartnerAdmin from './PartnerAdmin';
import PriceInquiryAdmin from './PriceInquiryAdmin';
import ContactInquiryAdmin from './ContactInquiryAdmin';

const t = (lang, de, en) => (lang === 'de' ? de : en);

const AdminHub = () => {
  const { isDark, toggleTheme } = useTheme();
  const { language } = useLanguage();
  const [authenticated, setAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [activeSection, setActiveSection] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get('section');
    return ['leads', 'inquiries', 'contacts', 'blog', 'partners'].includes(s) ? s : null;
  });
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) setAuthenticated(true);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      localStorage.setItem('adminToken', data.token);
      setAuthenticated(true);
    } catch (err) {
      setLoginError(err.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setAuthenticated(false);
    setActiveSection(null);
  };

  if (!authenticated) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
        <form onSubmit={handleLogin} className={`w-full max-w-sm mx-4 rounded-2xl p-8 border ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
          <h1 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Admin-Bereich</h1>
          <p className={`text-sm mb-6 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {t(language, 'Zugang zum Verwaltungsbereich', 'Access to admin area')}
          </p>
          <Input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="E-Mail"
            data-testid="admin-hub-email"
            className={`mb-3 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}
          />
          <Input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Passwort"
            data-testid="admin-hub-password"
            className={`mb-3 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}
          />
          {loginError && <p className="text-red-400 text-sm mb-3">{loginError}</p>}
          <Button type="submit" className="w-full bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white" data-testid="admin-hub-login-btn">
            {t(language, 'Anmelden', 'Login')}
          </Button>
        </form>
      </div>
    );
  }

  if (activeSection === 'blog') {
    return (
      <div>
        <div className={`sticky top-0 z-50 backdrop-blur-xl border-b ${isDark ? 'bg-[#0a0a0a]/90 border-white/5' : 'bg-white/90 border-gray-200'}`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center">
            <button
              onClick={() => setActiveSection(null)}
              className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg transition-colors ${isDark ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
              data-testid="admin-back-to-hub"
            >
              <ArrowLeft className="w-4 h-4" />
              {t(language, 'Zurück zur Übersicht', 'Back to overview')}
            </button>
          </div>
        </div>
        <BlogAdmin skipAuth={true} />
      </div>
    );
  }

  if (activeSection === 'partners') {
    return (
      <div>
        <div className={`sticky top-0 z-50 backdrop-blur-xl border-b ${isDark ? 'bg-[#0a0a0a]/90 border-white/5' : 'bg-white/90 border-gray-200'}`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center">
            <button
              onClick={() => setActiveSection(null)}
              className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg transition-colors ${isDark ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
              data-testid="admin-back-to-hub"
            >
              <ArrowLeft className="w-4 h-4" />
              {t(language, 'Zurück zur Übersicht', 'Back to overview')}
            </button>
          </div>
        </div>
        <PartnerAdmin skipAuth={true} />
      </div>
    );
  }

  if (activeSection === 'inquiries') {
    return (
      <div>
        <div className={`sticky top-0 z-50 backdrop-blur-xl border-b ${isDark ? 'bg-[#0a0a0a]/90 border-white/5' : 'bg-white/90 border-gray-200'}`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center">
            <button
              onClick={() => setActiveSection(null)}
              className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg transition-colors ${isDark ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
              data-testid="admin-back-to-hub"
            >
              <ArrowLeft className="w-4 h-4" />
              {t(language, 'Zurück zur Übersicht', 'Back to overview')}
            </button>
          </div>
        </div>
        <PriceInquiryAdmin skipAuth={true} />
      </div>
    );
  }

  if (activeSection === 'contacts') {
    return (
      <div>
        <div className={`sticky top-0 z-50 backdrop-blur-xl border-b ${isDark ? 'bg-[#0a0a0a]/90 border-white/5' : 'bg-white/90 border-gray-200'}`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center">
            <button
              onClick={() => setActiveSection(null)}
              className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg transition-colors ${isDark ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
              data-testid="admin-back-to-hub"
            >
              <ArrowLeft className="w-4 h-4" />
              {t(language, 'Zurück zur Übersicht', 'Back to overview')}
            </button>
          </div>
        </div>
        <ContactInquiryAdmin skipAuth={true} />
      </div>
    );
  }

  if (activeSection === 'leads') {
    return (
      <div>
        <div className={`sticky top-0 z-50 backdrop-blur-xl border-b ${isDark ? 'bg-[#0a0a0a]/90 border-white/5' : 'bg-white/90 border-gray-200'}`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center">
            <button
              onClick={() => setActiveSection(null)}
              className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg transition-colors ${isDark ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
              data-testid="admin-back-to-hub"
            >
              <ArrowLeft className="w-4 h-4" />
              {t(language, 'Zurück zur Übersicht', 'Back to overview')}
            </button>
          </div>
        </div>
        <LeadBoard />
      </div>
    );
  }

  // Hub overview
  const sections = [
    {
      id: 'leads',
      icon: <KanbanSquare className="w-8 h-8" />,
      title: t(language, 'Lead-Board', 'Lead Board'),
      description: t(language, 'Anfragen als Kanban-Board organisieren: Wer wurde angerufen, wer braucht eine Wiedervorlage?', 'Organize inquiries as a Kanban board: who was called, who needs a follow-up?'),
      color: 'from-sky-500/20 to-sky-600/10',
      iconColor: 'text-sky-400',
    },
    {
      id: 'blog',
      icon: <FileText className="w-8 h-8" />,
      title: t(language, 'Blog-Verwaltung', 'Blog Management'),
      description: t(language, 'Artikel erstellen, bearbeiten und veröffentlichen', 'Create, edit and publish articles'),
      color: 'from-blue-500/20 to-blue-600/10',
      iconColor: 'text-blue-400',
    },
    {
      id: 'partners',
      icon: <Users className="w-8 h-8" />,
      title: t(language, 'Partner-Verwaltung', 'Partner Management'),
      description: t(language, 'Partner verwalten, Empfehlungen tracken, Provisionen auszahlen', 'Manage partners, track referrals, pay commissions'),
      color: 'from-emerald-500/20 to-emerald-600/10',
      iconColor: 'text-emerald-400',
    },
    {
      id: 'inquiries',
      icon: <Calculator className="w-8 h-8" />,
      title: t(language, 'Preisrechner-Anfragen', 'Price Calculator Inquiries'),
      description: t(language, 'Anfragen einsehen, KI-Antworten generieren und per E-Mail beantworten', 'View inquiries, generate AI replies and respond via email'),
      color: 'from-amber-500/20 to-amber-600/10',
      iconColor: 'text-amber-400',
    },
    {
      id: 'contacts',
      icon: <MessageSquare className="w-8 h-8" />,
      title: t(language, 'Kontaktanfragen', 'Contact Inquiries'),
      description: t(language, 'Nachrichten aus dem Kontaktformular einsehen und beantworten', 'View and respond to contact form messages'),
      color: 'from-purple-500/20 to-purple-600/10',
      iconColor: 'text-purple-400',
    },
  ];

  return (
    <div className={`min-h-screen transition-colors ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
      <header className={`border-b ${isDark ? 'bg-[#0a0a0a] border-white/5' : 'bg-white border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t(language, 'Admin-Bereich', 'Admin Area')}
            </h1>
            <p className={`text-sm mt-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
              Dion Hair Clinic
            </p>
          </div>
          <button onClick={toggleTheme} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-500'}`}>
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <button onClick={handleLogout} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-500'}`} data-testid="admin-logout-btn">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`text-left rounded-2xl p-8 border transition-all hover:scale-[1.02] active:scale-[0.98] ${isDark ? 'bg-[#111] border-white/5 hover:border-white/10' : 'bg-white border-gray-100 shadow-sm hover:shadow-md'}`}
              data-testid={`admin-section-${s.id}`}
            >
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-5 bg-gradient-to-br ${s.color}`}>
                <span className={s.iconColor}>{s.icon}</span>
              </div>
              <h2 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.title}</h2>
              <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{s.description}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminHub;
